import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  ArrowBack, 
  PlayArrow, 
  CheckCircle, 
  AccessTime,
  VideoLibrary,
  ArrowForward,
  Lock
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Course, Lesson, Purchase, Unlocks, LessonProgress } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const LessonDetail: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [allLessons, setAllLessons] = useState<Lesson[]>([]);
  const [userPurchases, setUserPurchases] = useState<Purchase[]>([]);
  const [userUnlocks, setUserUnlocks] = useState<Unlocks[]>([]);
  const [lessonProgress, setLessonProgress] = useState<LessonProgress | null>(null);
  const [allProgress, setAllProgress] = useState<LessonProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingComplete, setMarkingComplete] = useState(false);
  const videoIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const fetchLessonData = async () => {
      if (!courseId || !lessonId) return;
      
      try {
        setLoading(true);
        const { results: allCourses } = await apiService.getPublishedCourses(1, 500);
        const courseData = allCourses.find(course => course.hashed === courseId);
        if (!courseData) {
          throw new Error('Course not found');
        }
        
        // Get all lessons for this course
        const lessonsData = await apiService.getLessonsByCourse(courseData.id);
        const currentLesson = lessonsData.find(lesson => lesson.hashed === lessonId);
        
        if (!currentLesson) {
          throw new Error('Lesson not found');
        }
        
        // Fetch user purchases/unlocks/progress if authenticated
        let purchases: Purchase[] = [];
        let unlocks: Unlocks[] = [];
        let progressList: LessonProgress[] = [];
        
        if (isAuthenticated) {
          try {
            purchases = await apiService.getMyPurchases();
          } catch (error) {
            console.log('Could not fetch purchases:', error);
          }
          try {
            unlocks = await apiService.getMyUnlocks();
          } catch (error) {
            console.log('Could not fetch unlocks:', error);
          }
          try {
            // Fetch all progress to find the one for this lesson
            progressList = await apiService.getMyProgress();
          } catch (error) {
            console.log('Could not fetch progress:', error);
          }
        }
        
        setCourse(courseData);
        setLesson(currentLesson);
        setAllLessons(lessonsData);
        setUserPurchases(purchases);
        setUserUnlocks(unlocks);
        setAllProgress(progressList);
        
        // Set existing progress for current lesson (clear when switching to a lesson with no progress)
        const existingProgress = progressList.find(p => p.lesson.id === currentLesson.id);
        setLessonProgress(existingProgress || null);
        
        // Note: video_url is already set by the API based on user permissions
        // No need to construct URL here - the API handles it securely
      } catch (error) {
        console.error('Error fetching lesson data:', error);
        navigate(`/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchLessonData();
  }, [courseId, lessonId, navigate, isAuthenticated]);

  // Bunny Player Progress Tracking via postMessage API - parent MUST subscribe to receive events
  useEffect(() => {
    if (!lesson || !isAuthenticated || !lesson.video_url) return;

    let lastUpdateTime = 0;
    let subscribed = false;
    const UPDATE_INTERVAL = 5000; // Update server every 5 seconds
    let currentProgress = 0;

    const getIframeOrigin = (): string => {
      try {
        if (lesson?.video_url) {
          return new URL(lesson.video_url).origin;
        }
      } catch (_) {}
      return 'https://iframe.mediadelivery.net';
    };

    const subscribeToPlayerEvents = () => {
      const iframe = videoIframeRef.current;
      if (!iframe?.contentWindow || subscribed) return;
      subscribed = true;
      const origin = getIframeOrigin();
      const base = { context: 'player.js', version: '0.0.11', method: 'addEventListener' };
      ['ended', 'timeupdate'].forEach((evt) => {
        iframe.contentWindow!.postMessage(
          JSON.stringify({ ...base, value: evt, listener: `progress-${evt}` }),
          origin
        );
      });
    };

    const handleBunnyMessage = async (event: MessageEvent) => {
      // Verify message is from Bunny CDN
      if (!event.origin.includes('mediadelivery.net')) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!data) return;
        // Player.js spec uses "event" (Bunny uses this); accept "type" as fallback
        const eventName = data.event ?? data.type;
        if (!eventName) return;
        if (data.context && data.context !== 'player.js') return;

        if (eventName === 'ready') {
          subscribeToPlayerEvents();
        }

        // Handle different Bunny player events
        switch (eventName) {
          case 'timeupdate': {
            // Player.js spec: timeupdate has value: { seconds, duration }
            const val = data.value ?? data;
            const duration = val.duration ?? data.duration;
            const current = val.seconds ?? val.currentTime;
            if (duration != null && current != null && duration > 0) {
              const percentage = Math.floor((Number(current) / Number(duration)) * 100);
              currentProgress = percentage;
              
              const now = Date.now();
              if (now - lastUpdateTime > UPDATE_INTERVAL && percentage > 0) {
                lastUpdateTime = now;
                
                try {
                  const updated = await apiService.createOrUpdateProgress(lesson.id, {
                    progress_percentage: percentage
                  });
                  setLessonProgress(updated);
                  setAllProgress(prev => {
                    const idx = prev.findIndex(p => p.id === updated.id);
                    if (idx !== -1) {
                      const newArr = [...prev];
                      newArr[idx] = updated;
                      return newArr;
                    }
                    return [...prev, updated];
                  });
                  console.log(`Progress updated: ${percentage}%`);
                } catch (err) {
                  console.error('Failed to update progress:', err);
                }
              }
            }
            break;
          }

          case 'ended':
            // Mark lesson as completed when video ends
            try {
              if (lessonProgress) {
                const completed = await apiService.markLessonCompleted(lessonProgress.id);
                setLessonProgress(completed);
                setAllProgress(prev => {
                  const idx = prev.findIndex(p => p.id === completed.id);
                  if (idx !== -1) {
                    const newArr = [...prev];
                    newArr[idx] = completed;
                    return newArr;
                  }
                  return [...prev, completed];
                });
              } else {
                const created = await apiService.createOrUpdateProgress(lesson.id, {
                  progress_percentage: 100,
                  is_completed: true
                });
                setLessonProgress(created);
                setAllProgress(prev => [...prev, created]);
              }
              console.log('Lesson marked as completed');
            } catch (err) {
              console.error('Failed to mark completed:', err);
            }
            break;

          case 'play':
            console.log('Video started playing');
            break;

          case 'pause':
            console.log('Video paused');
            break;
        }
      } catch (err) {
        console.error('Error handling Bunny message:', err);
      }
    };

    // Add message listener
    window.addEventListener('message', handleBunnyMessage);

    // Fallback: subscribe after iframe has had time to load (in case "ready" format differs)
    const t1 = window.setTimeout(subscribeToPlayerEvents, 1500);
    const t2 = window.setTimeout(() => {
      subscribed = false;
      subscribeToPlayerEvents();
    }, 4000);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleBunnyMessage);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [lesson, isAuthenticated, lessonProgress]);

  // Helper function to check if user has access to this course (unlocks)
  const hasPurchasedCourse = (): boolean => {
    if (!course || !isAuthenticated) return false;

    // 1) Prefer purchases as source of truth for expiry (matches backend check_user_course_access)
    const coursePurchases = userPurchases.filter(
      purchase => purchase.course_id === course.id && purchase.is_verified
    );
    if (coursePurchases.length > 0) {
      const hasActivePurchase = coursePurchases.some(
        purchase => purchase.is_active && !purchase.is_expired
      );
      // If there is any purchase record for this course, we NEVER fall back to unlocks.
      // Expiry is fully controlled by the purchase.
      return hasActivePurchase;
    }

    // 2) If there is no purchase, fall back to unlocks (e.g. manual unlocks, legacy data)
    const unlock = userUnlocks.find(unlock => unlock.course_id === course.id);
    if (unlock) {
      return unlock.is_active && !unlock.is_expired;
    }

    return false;
  };

  // Helper: has the user ever purchased/unlocked this course (even if now expired)?
  const hasEverPurchasedCourse = (): boolean => {
    if (!course || !isAuthenticated) return false;
    const hadUnlock = userUnlocks.some(unlock => unlock.course_id === course.id);
    const hadPurchase = userPurchases.some(
      purchase => purchase.course_id === course.id && purchase.is_verified
    );
    return hadUnlock || hadPurchase;
  };

  // Helper: specific flag for "had access before, but it's no longer active"
  const hasExpiredAccess = (): boolean => {
    return !hasPurchasedCourse() && hasEverPurchasedCourse();
  };

  // Helper function to handle lesson navigation
  const handleLessonClick = (targetLesson: Lesson) => {
    if (hasPurchasedCourse()) {
      navigate(`/courses/${course?.hashed}/lessons/${targetLesson.hashed}`);
    }
  };

  // Manual mark as complete handler (fallback if postMessage doesn't work)
  const handleMarkComplete = async () => {
    if (!lesson || markingComplete) return;
    
    setMarkingComplete(true);
    try {
      if (lessonProgress) {
        const completed = await apiService.markLessonCompleted(lessonProgress.id);
        setLessonProgress(completed);
        setAllProgress(prev => {
          const idx = prev.findIndex(p => p.id === completed.id);
          if (idx !== -1) {
            const newArr = [...prev];
            newArr[idx] = completed;
            return newArr;
          }
          return [...prev, completed];
        });
      } else {
        const created = await apiService.createOrUpdateProgress(lesson.id, {
          progress_percentage: 100,
          is_completed: true
        });
        setLessonProgress(created);
        setAllProgress(prev => [...prev, created]);
      }
    } catch (err) {
      console.error('Failed to mark as complete:', err);
    } finally {
      setMarkingComplete(false);
    }
  };

  // Get current lesson index
  const currentLessonIndex = allLessons.findIndex(l => l.hashed === lessonId);
  const nextLesson = currentLessonIndex < allLessons.length - 1 ? allLessons[currentLessonIndex + 1] : null;
  const prevLesson = currentLessonIndex > 0 ? allLessons[currentLessonIndex - 1] : null;


  if (loading) {
    return <LoadingSpinner message="Loading lesson..." />;
  }

  if (!course || !lesson) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          Lesson not found
        </Typography>
      </Box>
    );
  }

  if (!hasPurchasedCourse()) {
    return (
      <Box>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Breadcrumb 
            items={[
              { label: 'Home', path: '/' },
              { label: 'Courses', path: '/courses' },
              { label: course.name, path: `/courses/${course.hashed}` },
              { label: lesson.title }
            ]} 
          />
          
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Lock sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              {hasExpiredAccess() ? '🔒 Access Expired' : '🔒 Lesson Locked'}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
              {hasExpiredAccess()
                ? 'Your access to this course has expired. Renew to unlock this lesson again.'
                : 'You need to purchase this course to access the lessons.'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(`/courses/${course.hashed}`)}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                },
              }}
            >
              Go to Course
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Home', path: '/' },
            { label: 'Courses', path: '/courses' },
            { label: course.name, path: `/courses/${course.hashed}` },
            { label: lesson.title }
          ]} 
        />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, mt: 4 }}>
          {/* Main Content - Video Player */}
          <Box sx={{ flex: '2 1 0', minWidth: 0 }}>
            {/* Video Player */}
            <Card 
              sx={{ 
                mb: 4,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: '250px', sm: '400px', md: '500px' },
                  backgroundColor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {lesson.video_url ? (
                  // User has access and video URL is provided by API
                  <iframe
                    ref={videoIframeRef}
                    src={lesson.video_url}
                    loading="lazy"
                    style={{
                      border: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    title={lesson.title}
                  />
                ) : lesson.has_video && !lesson.can_access_video ? (
                  // Lesson has video but user doesn't have access
                  <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
                    <Lock sx={{ fontSize: '3rem', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ opacity: 0.7 }}>
                      Please purchase this course to access the video
                    </Typography>
                  </Box>
                ) : lesson.has_video ? (
                  // Has video but URL not loaded yet
                  <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
                    <Typography variant="h6" sx={{ opacity: 0.7 }}>
                      Loading video...
                    </Typography>
                  </Box>
                ) : (
                  // No video available for this lesson
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <VideoLibrary sx={{ fontSize: '4rem', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ opacity: 0.7 }}>
                      No video available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Lesson Content */}
            <Card 
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.1)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PlayArrow sx={{ mr: 2, color: '#10b981', fontSize: '2rem' }} />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                    {lesson.title}
                  </Typography>
                  {lessonProgress?.is_completed && (
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Completed" 
                      color="success" 
                      variant="outlined"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>

                {/* Progress Indicator */}
                {lessonProgress && !lessonProgress.is_completed && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                        Learning Progress
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                        {lessonProgress.progress_percentage}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={lessonProgress.progress_percentage} 
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#10b981',
                          borderRadius: 1,
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Mark Complete Button */}
                {!lessonProgress?.is_completed && isAuthenticated && lesson.video_url && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleMarkComplete}
                        disabled={markingComplete}
                        startIcon={<CheckCircle />}
                      >
                        {markingComplete ? 'Marking...' : 'Mark Complete'}
                      </Button>
                    }
                  >
                    Progress is tracked automatically. You can also manually mark this lesson as complete.
                  </Alert>
                )}
                
                <Divider sx={{ mb: 3 }} />
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.8, 
                    color: 'text.secondary',
                    mb: 3,
                  }}
                >
                  {lesson.description}
                </Typography>

                {/* File Download Section */}
                {lesson.file && (
                  <Box 
                    sx={{ 
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(16, 185, 129, 0.05)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1,
                          backgroundColor: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Lesson File
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Download the attached file
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        href={lesson.file}
                        download
                        sx={{
                          backgroundColor: '#10b981',
                          '&:hover': {
                            backgroundColor: '#059669',
                          },
                        }}
                        startIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                        }
                      >
                        Download
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                  {prevLesson && (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={() => handleLessonClick(prevLesson)}
                      sx={{
                        borderColor: '#10b981',
                        color: '#10b981',
                        '&:hover': {
                          borderColor: '#059669',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        },
                      }}
                    >
                      Previous Lesson
                    </Button>
                  )}
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  {nextLesson ? (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => handleLessonClick(nextLesson)}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                        },
                      }}
                    >
                      Next Lesson
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/courses/${course.hashed}`)}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                        },
                      }}
                    >
                      Back to Course
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Sidebar - Course Lessons */}
          <Box sx={{ flex: '1 1 0', minWidth: '300px' }}>
            <Card 
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                position: 'sticky',
                top: 24,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VideoLibrary sx={{ mr: 2, color: '#10b981', fontSize: '1.5rem' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Course Lessons
                    </Typography>
                  </Box>
                  <Badge 
                    badgeContent={allLessons.length} 
                    color="primary" 
                  >
                    <Chip 
                      label={`${allLessons.length} lessons`} 
                      color="primary" 
                      variant="outlined"
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        borderColor: '#10b981',
                        color: '#10b981'
                      }}
                    />
                  </Badge>
                </Box>

                <List sx={{ p: 0 }}>
                  {allLessons.map((courseLesson, index) => {
                    const isCurrentLesson = courseLesson.hashed === lessonId;
                    const progress = allProgress.find(p => p.lesson.id === courseLesson.id);
                    const isCompleted = progress?.is_completed || false;
                    
                    return (
                      <ListItem
                        key={courseLesson.id}
                        sx={{
                          borderBottom: index === allLessons.length - 1 ? 'none' : '1px solid rgba(0, 0, 0, 0.05)',
                          p: 0,
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleLessonClick(courseLesson)}
                          selected={isCurrentLesson}
                          sx={{
                            p: 2,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(16, 185, 129, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                              },
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 0.05)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: isCurrentLesson
                                  ? 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)'
                                  : isCompleted
                                  ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                  : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                              }}
                            >
                              {isCompleted ? (
                                <CheckCircle sx={{ fontSize: '1.2rem' }} />
                              ) : (
                                <PlayArrow sx={{ fontSize: '1rem' }} />
                              )}
                            </Box>
                          </ListItemIcon>
                          
                          <ListItemText
                            primary={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: isCurrentLesson ? 600 : 500,
                                  color: isCurrentLesson ? 'text.primary' : 'text.secondary',
                                  fontSize: '0.9rem',
                                }}
                              >
                                {courseLesson.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <AccessTime sx={{ fontSize: '0.8rem', mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Lesson {index + 1}
                                  </Typography>
                                  {isCompleted && (
                                    <Chip 
                                      label="Completed" 
                                      size="small" 
                                      color="success"
                                      variant="outlined"
                                      sx={{ 
                                        ml: 1,
                                        fontSize: '0.7rem',
                                        height: 16,
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  )}
                                </Box>
                                {/* Progress bar for in-progress lessons */}
                                {progress && !isCompleted && progress.progress_percentage > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={progress.progress_percentage}
                                      sx={{
                                        height: 3,
                                        borderRadius: 1,
                                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: '#10b981',
                                        }
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      {progress.progress_percentage}%
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LessonDetail;
